import { ORDER_TYPES } from './orderType';

/**
 * The hero banner's content, ONE ENTRY PER ORDER TYPE.
 *
 * Config, not markup: GiftHero draws whatever it is handed, so adding a fourth
 * order type is an object here and a case in `resolveOrderType` — never a new
 * branch of JSX. Everything that differs between the three banners lives in
 * this file.
 *
 * ⚠️ THE HERO SHOWS PRODUCTS, NOT PICTURES (changed 2026-09-28). Each `images`
 * entry names a product by `css_name`, and GiftHero composites THIS STUDENT's
 * pose into it — the same `Item previewOnly` render the shelf tiles use, so the
 * banner and the shelf can never show a different child.
 *
 * It replaced two fixed JPEGs per order type, which could only ever be wrong:
 * the senior banner shipped an ELEMENTARY student on its magnet and a
 * Davis-branded announcement that was wrong at all 500-odd other schools, and
 * school + sports had no artwork at all.
 *
 * When a named product cannot be composited -- not on this customer's shelf, or
 * no pose has arrived yet -- the slot is simply left EMPTY and the banner's
 * colour shows through. It used to draw a dashed box captioned with the slot's
 * name, which was scaffolding that read as developer text to a customer; the
 * state it guarded is an ordinary production one, not a dev-only one.
 */

export const PRODUCT_GRID_ID = 'gift-products';

/**
 * Copy can name the student: `{{first_name}}` and `{{grade}}`.
 *
 * ⚠️ A PLACEHOLDER MUST NEVER REACH A CUSTOMER. Every template below carries a
 * `*Fallback` written for the case where the value is not known, and
 * `fillTemplate` returns null rather than a half-filled string so the caller
 * uses it. `GiftHero.test.jsx` asserts no rendered banner ever contains "{{" --
 * this page shipped literal placeholder copy once already.
 */
export const fillTemplate = (template, values) => {
  if (!template) return null;

  const keys = [...String(template).matchAll(/\{\{(\w+)\}\}/g)].map(([, key]) => key);
  if (keys.some((key) => !values[key])) return null;

  return String(template).replace(/\{\{(\w+)\}\}/g, (_, key) => values[key]);
};

// Grades that are a word, not a number.
const GRADE_WORDS = {
  K: 'kindergarten',
  TK: 'transitional kindergarten',
  PK: 'pre-K',
  PS: 'preschool',
};

const ordinal = (n) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;

  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
};

/**
 * "their {{grade}} year" -- so this has to read as a year, not as a number.
 *
 * ⚠️ THE COLUMN IS NOT CLEAN. Real values in production include "K", "PS",
 * "ST" (a STAFF record, not a grade at all) and "0", alongside plain numbers as
 * strings. Anything this cannot phrase honestly returns null and the caller
 * falls back to copy with no grade in it -- "their ST year" must never ship.
 */
export const gradeYear = (grade) => {
  const value = String(grade ?? '').trim().toUpperCase();

  if (GRADE_WORDS[value]) return GRADE_WORDS[value];

  if (/^\d{1,2}$/.test(value)) {
    const n = Number(value);

    // 0 is excluded deliberately: it is used for several things across schools
    // and means no one grade.
    if (n >= 1 && n <= 12) return `${ordinal(n)} grade`;
  }

  return null;
};

const HERO_CONFIG = {
  [ORDER_TYPES.SENIOR]: {
    headline: 'Senior Keepsakes',
    body: "Stylish Grad Announcements, magnets and more to celebrate {{first_name}}'s Senior Year.",
    // Used when we do not know the student's name.
    bodyFallback: 'Stylish Grad Announcements, magnets and more to celebrate their Senior Year.',
    buttonLabel: 'Shop announcements',
    background: 'var(--banner-gift)',
    // What a senior came for: the announcement, then the magnets.
    images: [
      { product: 'gradannouncement' },
      { product: 'magnet' },
    ],
  },

  [ORDER_TYPES.SCHOOL]: {
    headline: "{{first_name}}'s Keepsakes",
    // Used when we do not know the student's name -- the copy this replaced.
    headlineFallback: 'Picture Day Keepsakes',
    body: 'Magnets, photo gifts and more to always remember their {{grade}} year.',
    // Used when the grade cannot be phrased as a year -- see gradeYear.
    bodyFallback: 'Magnets, photo gifts and more to always remember this year.',
    buttonLabel: 'Shop gifts',
    background: 'var(--banner-gift)',
    // Picture day: the portrait they actually ordered, then the magnets.
    images: [
      { product: 'eightbytenG' },
      { product: 'magnet' },
    ],
  },

  [ORDER_TYPES.SPORTS]: {
    headline: 'Game day keepsakes',
    body: "Magnets, keychains and photo gifts made from your athlete's team and individual photos.",
    buttonLabel: 'Shop photo gifts',
    background: 'var(--banner-gift)',
    // Game day: magnets and a keychain, the two that sell on this shelf.
    images: [
      { product: 'magnet' },
      { product: 'keychain' },
    ],
  },

  /**
   * NO ORDER IN PROGRESS — a direct visit, or a pricelist whose season means
   * nothing to us.
   *
   * ⚠️ THE COPY MAKES NO CLAIM ABOUT A SESSION. Promising "the portraits from
   * your session" to someone we could not resolve an order for is a claim the
   * page cannot keep, so this entry stays deliberately general -- it is the
   * banner every visitor who arrives without a pricelist will read.
   *
   * It carried openly unfinished placeholder text until 2026-09-29, on the
   * reasoning that obviously-broken copy could not ship quietly. It very nearly
   * shipped to production exactly that way.
   */
  [ORDER_TYPES.UNKNOWN]: {
    headline: 'Photo keepsakes',
    body: 'Prints, magnets and photo gifts made from your photos.',
    buttonLabel: 'Shop all products',
    background: 'var(--banner-gift)',
    // Nothing senior-specific: this visitor may not be a senior at all.
    images: [
      { product: 'magnet' },
      { product: 'keychain' },
    ],
    isPlaceholder: true,
  },
};

export default HERO_CONFIG;

export const heroForOrderType = (orderType) => (
  HERO_CONFIG[orderType] || HERO_CONFIG[ORDER_TYPES.UNKNOWN]
);
